/**
 * Booking state persistence for auth flow
 * Saves booking progress to sessionStorage before login/signup
 * Restores state when user returns after authentication
 */

const STORAGE_KEY = 'pendingBookingState';
const EXPIRATION_MS = 30 * 60 * 1000; // 30 minutes

export interface PendingBookingState {
  businessId: string;
  userInfo: {
    fullName: string;
    email: string;
    phone: string;
    phonePrefix: string;
    comments?: string;
  };
  selectedServices: any[];
  selectedEmployee: any | null;
  selectedDate: string | null; // ISO string for serialization
  selectedTimeSlot: any | null;
  currentStage: number;
  savedAt: number;
}

/**
 * Save booking state before navigating to auth
 */
export const saveBookingState = (state: Omit<PendingBookingState, 'savedAt'>) => {
  const stateToSave: PendingBookingState = {
    ...state,
    // Convert Date to ISO string for serialization
    selectedDate: state.selectedDate ? new Date(state.selectedDate as any).toISOString() : null,
    savedAt: Date.now(),
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
};

/**
 * Load booking state after returning from auth
 * Returns null if no state, wrong business, or expired
 */
export const loadBookingState = (businessId: string): PendingBookingState | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const state = JSON.parse(raw) as PendingBookingState;

    // Validate: must be same business
    if (state.businessId !== businessId) {
      return null;
    }

    // Validate: not expired (30 minutes)
    if (Date.now() - state.savedAt > EXPIRATION_MS) {
      clearBookingState();
      return null;
    }

    return state;
  } catch (error) {
    console.error('Error loading booking state:', error);
    clearBookingState();
    return null;
  }
};

/**
 * Clear saved booking state (after successful booking or expiration)
 */
export const clearBookingState = () => {
  sessionStorage.removeItem(STORAGE_KEY);
};
